<template>
  <section class="panel" :aria-labelledby="side + '-heading'">
    <div class="panel__head">
      <span class="role-badge" :class="'role-badge--' + side" aria-hidden="true">{{ badge }}</span>
      <h3 :id="side + '-heading'">{{ heading }}</h3>
      <span class="hint">{{ hint }}</span>
    </div>

    <div class="panel__filters">
      <div class="field-group">
        <div class="field">
          <label :for="side + '-location'">Monitoring location</label>
          <p v-if="collisionNote" class="field-note field-note--warn">{{ collisionNote }}</p>
          <!--
            An autocomplete rather than a select: a tenant can hold hundreds of stations, so the
            list has to be narrowed by typing. It is the same control otherwise, and the reviewed
            markup around it is unchanged.
          -->
          <v-autocomplete
            :id="side + '-location'"
            :value="filter.LocationId"
            :items="locations"
            item-text="label"
            item-value="id"
            placeholder="Any location"
            clearable
            hide-details
            @input="set('LocationId', $event)"
          />
        </div>
      </div>

      <div class="field-group">
        <div class="field-row">
          <div class="field">
            <label :for="side + '-from'">Sampled from</label>
            <!--
              Typing is the primary path: reviewers set the same range repeatedly and clicking
              through a calendar each time is the slow part. The calendar is still here, behind
              the icon, so the reviewed control is unchanged for anyone who prefers it.
            -->
            <div class="date-input">
              <v-text-field
                :id="side + '-from'"
                :value="text.From"
                placeholder="Any date"
                hide-details
                @input="onTextInput('From', $event)"
                @blur="commitDate('From')"
                @keyup.enter="commitDate('From')"
                @keyup.esc="syncText"
              />
              <v-menu v-model="dateMenu.From" :close-on-content-click="false" offset-y>
                <template v-slot:activator="{ on }">
                  <v-btn icon small class="date-input__picker" v-on="on">
                    <v-icon small>date_range</v-icon>
                  </v-btn>
                </template>
                <v-date-picker
                  :value="dateValue('From')"
                  no-title
                  @input="pickDate('From', $event)"
                />
              </v-menu>
            </div>
          </div>
          <div class="field">
            <label :for="side + '-to'">Sampled to</label>
            <div class="date-input">
              <v-text-field
                :id="side + '-to'"
                :value="text.To"
                placeholder="Any date"
                hide-details
                @input="onTextInput('To', $event)"
                @blur="commitDate('To')"
                @keyup.enter="commitDate('To')"
                @keyup.esc="syncText"
              />
              <v-menu v-model="dateMenu.To" :close-on-content-click="false" offset-y>
                <template v-slot:activator="{ on }">
                  <v-btn icon small class="date-input__picker" v-on="on">
                    <v-icon small>date_range</v-icon>
                  </v-btn>
                </template>
                <v-date-picker
                  :value="dateValue('To')"
                  no-title
                  @input="pickDate('To', $event)"
                />
              </v-menu>
            </div>
          </div>
        </div>

        <!--
          The house date-range shortcuts, read from the same helper the rest of the application
          uses so the vocabulary and the arithmetic are not restated here.
        -->
        <div class="date-shortcuts">
          <v-menu offset-y>
            <template v-slot:activator="{ on }">
              <v-btn flat small class="date-shortcuts__btn" v-on="on">
                <v-icon left small>calendar_today</v-icon>
                {{ rangeLabel }}
                <v-icon right small>arrow_drop_down</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-tile
                v-for="shortcut in shortcuts"
                :key="shortcut.key"
                @click="applyShortcut(shortcut)"
              >
                <v-list-tile-title>{{ shortcut.name() }}</v-list-tile-title>
              </v-list-tile>
            </v-list>
          </v-menu>
          <v-btn
            v-if="hasRange"
            flat
            small
            class="date-shortcuts__clear"
            @click="clearDates"
          >
            <v-icon left small>clear</v-icon>
            Clear
          </v-btn>
        </div>

        <!--
          The client asked for the time-of-day window back. It is a per-side window applied to
          every day in the date range, not a shared one, so each side still filters its own
          samples and the two sides stay independent.
        -->
        <div class="field-row">
          <div class="field">
            <label :for="side + '-time-from'">Time from</label>
            <v-text-field
              :id="side + '-time-from'"
              :value="text.TimeFrom"
              type="time"
              placeholder="Any time"
              hide-details
              @input="onTextInput('TimeFrom', $event)"
              @blur="commitTime('TimeFrom')"
              @keyup.enter="commitTime('TimeFrom')"
              @keyup.esc="syncText"
            />
          </div>
          <div class="field">
            <label :for="side + '-time-to'">Time to</label>
            <v-text-field
              :id="side + '-time-to'"
              :value="text.TimeTo"
              type="time"
              placeholder="Any time"
              hide-details
              @input="onTextInput('TimeTo', $event)"
              @blur="commitTime('TimeTo')"
              @keyup.enter="commitTime('TimeTo')"
              @keyup.esc="syncText"
            />
          </div>
        </div>

        <p v-if="timeInverted" class="field-note field-note--warn">
          Time from is later than time to, so no sample on any day can match. Widen the window or
          clear one end.
        </p>
        <p class="field-note">
          Both dates are inclusive of the whole day, and the time window applies to every day in
          the range. Type a date as YYYY-MM-DD or pick it from the calendar.
        </p>
        <div class="field">
          <label :for="side + '-alias'">QA/QC alias</label>
          <p v-if="aliasCollisionNote" class="field-note field-note--warn">{{ aliasCollisionNote }}</p>
          <v-select
            :id="side + '-alias'"
            :value="filter.AliasLabels"
            :items="aliases"
            item-text="label"
            item-value="id"
            multiple
            chips
            deletable-chips
            small-chips
            placeholder="Any alias"
            hide-details
            @input="set('AliasLabels', $event)"
          />
          <p class="field-note">
            A sample matching <em>any</em> selected alias is kept. Each extra value widens this
            side's match set.
          </p>
        </div>
      </div>

      <div class="field-group">
        <div class="field">
          <label :for="side + '-media'">Media type</label>
          <v-select
            :id="side + '-media'"
            :value="filter.MediaTypes"
            :items="mediaTypes"
            item-text="label"
            item-value="id"
            multiple
            chips
            deletable-chips
            small-chips
            placeholder="Any media type"
            hide-details
            @input="set('MediaTypes', $event)"
          />
        </div>
        <p class="field-note">
          The two sides must share at least one media type. Analytes are matched on
          analyte + fraction.
        </p>
      </div>
    </div>
  </section>
</template>

<script>
  /*
   * One side of the pairing. The two sides are structurally symmetric and this component is
   * rendered once per side, so the reviewed geometry holds without duplicating the markup.
   *
   * The filter set is held in wire casing and the parent owns it, so every control emits the
   * whole side back rather than mutating the prop.
   */
  import moment from 'moment'
  import { prettyDateFormat, backendDateTimeFormat } from '@/components/mixins/MomentUtil'
  import { makeShortcuts } from '@/helpers/dateRange'

  const sortByIndex = (a, b) => (a.index > b.index ? 1 : -1)

  const pad2 = (value) => ('0' + value).slice(-2)

  /*
   * A reviewer typing a date has no reason to know the wire format, so the shapes people actually
   * type are accepted. Anything else is rejected rather than coerced, and the field falls back to
   * the committed value: a misread date is worse than a refused one.
   */
  const TYPED_DATE_FORMATS = ['YYYY-MM-DD', 'YYYY/MM/DD', 'MMM DD, YYYY', 'MM/DD/YYYY']

  /* null = cleared, a string = committed value, undefined = did not parse. */
  function parseTypedDate(text) {
    const raw = String(text || '').trim()
    if (!raw) return null
    const parsed = moment(raw, TYPED_DATE_FORMATS, true)
    return parsed.isValid() ? parsed.format(backendDateTimeFormat) : undefined
  }

  function parseTypedTime(text) {
    const raw = String(text || '').trim()
    if (!raw) return null
    const match = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(raw)
    if (!match) return undefined
    const hours = Number(match[1])
    const minutes = Number(match[2])
    const seconds = Number(match[3] || 0)
    if (hours > 23 || minutes > 59 || seconds > 59) return undefined
    return `${pad2(hours)}:${pad2(minutes)}:${pad2(seconds)}`
  }

  export default {
    name: 'RpdPairingFilters',

    props: {
      side: {
        type: String,
        required: true,
        validator: (value) => value === 'primary' || value === 'duplicate',
      },
      filter: {
        type: Object,
        required: true,
      },
      locations: {
        type: Array,
        default: () => [],
      },
      aliases: {
        type: Array,
        default: () => [],
      },
      mediaTypes: {
        type: Array,
        default: () => [],
      },
      collisionNote: {
        type: String,
        default: '',
      },
      aliasCollisionNote: {
        type: String,
        default: '',
      },
    },

    data() {
      return {
        dateMenu: {
          From: false,
          To: false,
        },
        /*
         * The text the reviewer is typing, kept apart from the committed filter so a half-typed
         * date never reaches the wire. It is resynced from the filter on every commit and on any
         * outside change, so the two cannot drift.
         */
        text: {
          From: '',
          To: '',
          TimeFrom: '',
          TimeTo: '',
        },
      }
    },

    watch: {
      filter: {
        handler() {
          this.syncText()
        },
        deep: true,
        immediate: true,
      },
    },

    computed: {
      badge() {
        return this.side === 'primary' ? 'P' : 'D'
      },

      heading() {
        return this.side === 'primary' ? 'Primary' : 'Duplicate'
      },

      hint() {
        return this.side === 'primary'
          ? 'As reported by the lab'
          : 'The field duplicate of the primary sample'
      },

      /* The application's own shortcut set, so "Past month" means here what it means elsewhere. */
      shortcuts() {
        return Object.values(makeShortcuts(this.makeMoment))
          .sort(sortByIndex)
          .filter((shortcut) => !shortcut.hidden)
      },

      /* The shortcut the current range is equal to, so the control can name it back. */
      selectedShortcut() {
        const start = this.momentValue('From')
        const end = this.momentValue('To')
        const none = {
          start: null,
          end: null,
        }
        return Object.values(makeShortcuts(this.makeMoment))
          .sort(sortByIndex)
          .find((shortcut) => shortcut.isMatch(start, end, none))
      },

      rangeLabel() {
        if (!this.selectedShortcut) return 'Any dates'
        return this.selectedShortcut.name(this.momentValue('From'), this.momentValue('To'))
      },

      hasDates() {
        return Boolean(this.dateValue('From') || this.dateValue('To'))
      },

      /* The time window is meaningless as a bound but still worth clearing, so it counts here. */
      hasRange() {
        return this.hasDates || Boolean(this.timeValue('TimeFrom') || this.timeValue('TimeTo'))
      },

      /*
       * An inverted window matches nothing. The server applies the two bounds literally rather than
       * treating them as an overnight wrap, so the reviewer is told here instead of being handed an
       * empty report with no explanation.
       */
      timeInverted() {
        const from = this.timeValue('TimeFrom')
        const to = this.timeValue('TimeTo')
        return Boolean(from && to && from > to)
      },
    },

    methods: {
      /* A date picker takes and returns the same yyyy-MM-dd string the routes take. */
      dateValue(field) {
        const value = this.filter[field]
        if (!value) return null
        return String(value).slice(0, 10)
      },

      momentValue(field) {
        const value = this.dateValue(field)
        return value ? moment(value) : null
      },

      prettyDate(field) {
        const value = this.momentValue(field)
        if (!value || !value.isValid()) return ''
        return value.format(prettyDateFormat)
      },

      /* The time input and the wire format differ only in the seconds, which the input omits. */
      timeValue(field) {
        const value = this.filter[field]
        if (!value) return ''
        const match = /^(\d{1,2}):(\d{2})/.exec(String(value))
        return match ? `${pad2(match[1])}:${match[2]}` : ''
      },

      /* Everything the reviewer sees, rebuilt from the committed filter. */
      syncText() {
        this.text = {
          From: this.prettyDate('From'),
          To: this.prettyDate('To'),
          TimeFrom: this.timeValue('TimeFrom'),
          TimeTo: this.timeValue('TimeTo'),
        }
      },

      onTextInput(field, value) {
        this.text = {
          ...this.text,
          [field]: value,
        }
      },

      commitDate(field) {
        const parsed = parseTypedDate(this.text[field])
        if (parsed !== undefined) this.setDate(field, parsed)
        this.syncText()
      },

      commitTime(field) {
        const parsed = parseTypedTime(this.text[field])
        if (parsed !== undefined) this.set(field, parsed)
        this.syncText()
      },

      /* The picker is a moment's work: take the day and close. */
      pickDate(field, value) {
        this.dateMenu[field] = false
        this.setDate(field, value)
      },

      applyShortcut(shortcut) {
        if (shortcut.key === 'all') {
          this.clearDates()
          return
        }

        const start = moment().startOf('day')
        const end = moment().startOf('day')
        const range = shortcut.make(start, end)

        this.setDates(
          range.start ? range.start.format(backendDateTimeFormat) : null,
          range.end ? range.end.format(backendDateTimeFormat) : null
        )
      },

      /* Both ends move together, so the pair is emitted once rather than twice. */
      setDates(from, to) {
        this.$emit('update:filter', { ...this.filter, From: from, To: to })
      },

      clearDates() {
        this.$emit('update:filter', {
          ...this.filter,
          From: null,
          To: null,
          TimeFrom: null,
          TimeTo: null,
        })
      },

      makeMoment(value) {
        return moment(value)
      },

      set(field, value) {
        this.$emit('update:filter', { ...this.filter, [field]: value })
      },

      setDate(field, value) {
        this.set(field, value || null)
      },
    },
  }
</script>
